import React, {useEffect, useRef, useState} from "react";

interface CustomDropdownProps {
    label: string;
    placeholder: string;
    options: {value: string; title: string; description?: string}[];
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    required?: boolean;
    testId?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({label, placeholder, options, value, onChange, disabled, required, testId}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);

    return (
        <div className="odq-custom-dropdown" ref={ref} data-testid={testId}>
            <label className="odq-custom-dropdown__label">
                {label}
                {required && <span className="odq-custom-dropdown__required" aria-hidden="true"> *</span>}
            </label>
            <button
                className={`odq-custom-dropdown__trigger ${disabled ? "odq-custom-dropdown__trigger--disabled" : ""}`}
                onClick={() => !disabled && setOpen(!open)}
                type="button"
                data-testid={`${testId}-trigger`}
            >
                <span className={selectedOption ? "" : "odq-custom-dropdown__placeholder"}>
                    {selectedOption ? selectedOption.title : placeholder}
                </span>
                <span className="odq-custom-dropdown__arrow">{open ? "▲" : "▼"}</span>
            </button>
            {open && (
                <div className="odq-custom-dropdown__list" data-testid={`${testId}-list`}>
                    {options.map(opt => (
                        <div
                            key={opt.value}
                            className={`odq-custom-dropdown__option ${opt.value === value ? "odq-custom-dropdown__option--selected" : ""}`}
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            data-testid={`${testId}-option-${opt.value}`}
                        >
                            <div className="odq-custom-dropdown__option-title">{opt.title}</div>
                            {opt.description && (
                                <div className="odq-custom-dropdown__option-desc">{opt.description}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CustomDropdown;
